import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useSearchParams } from "react-router";
// FSD VIOLATION: Shared importing from entities. This hook provides validation state
// used by tab navigation. Core validation hook used across all layers.
// eslint-disable-next-line no-restricted-imports
import { useArtwork, useArtworkForm } from "@design-lab/entities/artwork";
// eslint-disable-next-line no-restricted-imports
import { useCompositionForm, useCurrentComposition } from "@design-lab/entities/composition";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { useVisitedTabs } from "./useVisitedTabs";

export function useValidation() {
  const { form: artworkForm } = useArtworkForm();
  const artworkFormValues = useWatch({ control: artworkForm.control });
  const { form: compositionForm } = useCompositionForm();
  const compositionFormValues = useWatch({ control: compositionForm.control });
  const { front, back, someArtworkLoading } = useArtwork();
  const { currentComposition } = useCurrentComposition();
  const [searchParams] = useSearchParams();

  const [visitedTabs] = useVisitedTabs();
  const experiments = useExperiments();
  const showSubTabs = useMemo(
    () => shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs),
    [experiments?.abDesignLabReorderTabs]
  );

  // Detect redirect from "Add to Cart" on saved project with no quantities
  const isAddToCartIntent = searchParams.get("intent") === "atc";

  const frontLoadingStatus = front.state.hasTag("loading");
  const isFrontArtworkProcessing = Boolean(frontLoadingStatus);
  const isFrontArtworkUploaded = Boolean(
    artworkFormValues?.front?.artworkId || artworkFormValues?.frontLeftChest?.artworkId
  );

  const backLoadingStatus = back.state.hasTag("loading");
  const isBackArtworkProcessing = Boolean(backLoadingStatus);
  const isBackArtworkUploaded = Boolean(artworkFormValues?.back?.artworkId);

  const isArtworkUploaded = isFrontArtworkUploaded || isBackArtworkUploaded;

  const addedCompositions = compositionFormValues?.addedCompositions ?? [];
  const currentAddedComposition = addedCompositions.find(
    composition => composition.id === currentComposition?.id
  );

  const isQuantityValid = Object.values(currentAddedComposition?.variants || {}).some(
    quantity => Number(quantity) > 0
  );

  const isAllQuantitiesValid =
    compositionFormValues?.addedCompositions?.some(composition =>
      Object.values(composition.variants || {}).some(quantity => Number(quantity) > 0)
    ) || false;

  const isAtLeastOneArtworkProcessed =
    isArtworkUploaded && (!isFrontArtworkProcessing || !isBackArtworkProcessing);

  const isFileSelected = Boolean(
    front.state.context.visitedStates.includes("progress") ||
    front.state.matches("finished") ||
    back.state.context.visitedStates.includes("progress") ||
    back.state.matches("finished")
  );

  const isFrontArtworkEnhanced = front.state.matches("finished");
  const isBackArtworkEnhanced = back.state.matches("finished");
  const isArtworkEnhanced = isFrontArtworkEnhanced || isBackArtworkEnhanced;

  // In A/B test with sub-tabs, check for "design" tab; otherwise check for "front" and "back" tabs
  // For candidate variant, enable add to cart cta in DL when redirected from saved project Add to Cart and quantities are valid
  const isDesignTabsVisited = showSubTabs
    ? visitedTabs.has("design") || (isAddToCartIntent && isArtworkUploaded && isAllQuantitiesValid)
    : (artworkFormValues?.front?.artworkId || artworkFormValues?.frontLeftChest?.artworkId
        ? visitedTabs.has("front")
        : true) && (artworkFormValues?.back?.artworkId ? visitedTabs.has("back") : true);

  return {
    isFileSelected,
    isArtworkUploaded,
    isArtworkEnhanced,
    isQuantityValid,
    isAllQuantitiesValid,
    isAtLeastOneArtworkProcessed,
    isSomeArtworkLoading: someArtworkLoading,
    isFrontArtworkUploaded,
    isBackArtworkUploaded,
    visitedTabs,
    isDesignTabsVisited,
    isFrontArtworkEnhanced,
    isBackArtworkEnhanced,
    showSubTabs
  };
}
